import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Textarea, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { adminSupportBulkImportSchema, AdminSupportBulkImportSchemaInput, AdminSupportBulkImportSchemaOutput } from "view/admin/utils/admin.schema";

import { SaveAdminFaqEntriesDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface SupportEntryBulkImportDialogProps {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

const SupportEntryBulkImportDialog = ({ open, onClose, refetch }: SupportEntryBulkImportDialogProps) => {
	const [ saveAdminFaqEntries, { loading } ] = useMutation(SaveAdminFaqEntriesDocument);

	const { handleSubmit, control, reset, formState } = useForm<AdminSupportBulkImportSchemaInput, unknown, AdminSupportBulkImportSchemaOutput>({
		resolver: zodResolver(adminSupportBulkImportSchema),
		defaultValues: {
			json: "",
		},
	});

	const save = (data: AdminSupportBulkImportSchemaOutput) => {

		const entries = JSON.parse(data.json) as {
			id?: number;
			question: string;
			answer: string;
			category?: { id: number };
			sequenceNumber: number;
		}[];

		saveAdminFaqEntries({
			variables: {
				inputs: entries.map(entry => ({
					id: entry.id,
					question: entry.question,
					answer: entry.answer,
					category: entry.category?.id,
					sequenceNumber: entry.sequenceNumber,
				})),
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				json: "",
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "ADMIN_FAQ_ENTRIES_BULK_IMPORT_TITLE" })}
			form="adminFaqEntriesBulkImportForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="adminFaqEntriesBulkImportForm">
				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_FAQ_BULK_IMPORT_JSON_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="json"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Textarea {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};
export default SupportEntryBulkImportDialog;
